import { useState } from "react";
import { Link } from "react-router-dom";
import { Camera, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const FIELD = "h-12 w-full rounded-sm border border-[var(--line)] bg-[var(--ink-2)] px-3 text-base outline-none focus:border-[var(--gold)]";

function PhotoInput({ label, file, setFile, testid }) {
  const url = file ? URL.createObjectURL(file) : null;
  return (
    <label data-testid={`${testid}-label`} className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-sm border border-dashed border-[var(--gold)]/60 bg-[var(--ink-2)] text-center text-sm text-[var(--parchment)]/85 hover:bg-[rgba(217,180,106,0.06)]">
      {url ? <img src={url} alt={label} className="h-full w-full object-cover" /> : <><Camera className="h-6 w-6 text-[var(--gold)]" aria-hidden="true" />{label}<span className="text-xs text-[var(--parchment)]/70">JPG, PNG · 12 Mo max</span></>}
      <input data-testid={testid} type="file" accept="image/*" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] || null)} />
    </label>
  );
}

export default function ShareBeforeAfter() {
  useSeo({ title: "Partagez votre avant / après | Ozentya", description: "Envoyez vos photos avant / après Ozentya : elles seront publiées après validation, avec votre accord.", path: "/partager-mon-avant-apres", noindex: true });
  const [f, setF] = useState({ name: "", email: "", city: "", product: "", duration: "", quote: "" });
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    if (!before || !after) { toast.error("Ajoutez vos deux photos : avant et après."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v.trim()));
      fd.append("consent", consent ? "true" : "false");
      fd.append("before", before);
      fd.append("after", after);
      await api.post("/before-after", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  if (done) {
    return (
      <main data-testid="share-ba-done" className="mx-auto max-w-2xl px-5 pb-32 pt-44 text-center md:px-10">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--gold)]" aria-hidden="true" />
        <h1 className="mt-6 font-display text-4xl">Merci pour votre partage !</h1>
        <p className="mt-4 text-[var(--parchment)]/85">Nous allons regarder vos photos. Elles seront publiées sur le site après validation par notre équipe.</p>
        <Link to="/" data-testid="share-ba-home" className="mt-8 inline-flex h-12 items-center rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink)]">Retour à l'accueil</Link>
      </main>
    );
  }

  return (
    <main data-testid="share-ba-page" className="mx-auto max-w-3xl px-5 pb-32 pt-44 md:px-10">
      <p className="eyebrow mb-6">Votre résultat compte</p>
      <h1 className="font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Partagez votre avant / après</h1>
      <p className="mt-6 text-base leading-relaxed text-[var(--parchment)]/85">Vous avez suivi une routine Ozentya ? Vos photos peuvent encourager d'autres femmes. Elles sont publiées seulement après validation par notre équipe et, une fois en ligne, <b className="text-[var(--gold-soft)]">vous recevez un code de -15 %</b> sur votre prochaine commande. Prenez-les si possible à la même lumière et sous le même angle.</p>
      <form onSubmit={send} className="mt-10 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <PhotoInput label="Photo avant" file={before} setFile={setBefore} testid="share-ba-before" />
          <PhotoInput label="Photo après" file={after} setFile={setAfter} testid="share-ba-after" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <input data-testid="share-ba-name" required maxLength={60} value={f.name} onChange={set("name")} placeholder="Prénom (affiché)" className={FIELD} />
          <input data-testid="share-ba-email" required type="email" value={f.email} onChange={set("email")} placeholder="Email (jamais affiché)" className={FIELD} />
          <input data-testid="share-ba-city" maxLength={60} value={f.city} onChange={set("city")} placeholder="Ville (facultatif)" className={FIELD} />
          <input data-testid="share-ba-duration" maxLength={40} value={f.duration} onChange={set("duration")} placeholder="Durée (ex. 3 mois)" className={FIELD} />
        </div>
        <input data-testid="share-ba-product" maxLength={80} value={f.product} onChange={set("product")} placeholder="Soins utilisés (ex. Extrême Repouss)" className={FIELD} />
        <textarea data-testid="share-ba-quote" maxLength={600} rows={4} value={f.quote} onChange={set("quote")} placeholder="Votre témoignage en quelques mots (facultatif)"
          className="w-full rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-3 text-base outline-none focus:border-[var(--gold)]" />
        <label className="flex items-start gap-2 text-xs leading-relaxed text-[var(--parchment)]/85">
          <input data-testid="share-ba-consent" type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          Je certifie être la personne photographiée (ou son représentant légal) et j'autorise Ozentya à publier ces photos, mon prénom, ma ville et mon témoignage sur ozentya.net et ses réseaux. Je peux demander leur retrait à tout moment par email.
        </label>
        <button data-testid="share-ba-submit" disabled={busy} className="flex h-12 items-center gap-2 rounded-full bg-[var(--gold)] px-8 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink)] hover:bg-[var(--gold-soft)] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Envoyer mes photos
        </button>
      </form>
    </main>
  );
}
